import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errCode } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { LudoBoard, LudoTable } from "@/components/LudoBoard";
import { Die } from "@/components/Dice";
import { SoundToggle } from "@/components/SoundToggle";
import { ShareResult } from "@/components/ShareResult";
import { COLORS } from "@/lib/ludoBoard";
import { playCapture, playDice, playHome, playSteps, playWin } from "@/lib/sound";

const Pill = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`h-9 rounded-full px-4 text-xs font-semibold transition-colors ${on ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 text-slate-300"}`}>{children}</button>
);

const sound = (ev) => {
  if (!ev) return;
  if (ev.type === "roll") { playDice(); return; }
  playSteps(ev.from === -1 ? 1 : ev.to - ev.from);
  if (ev.outcome === "won") setTimeout(playWin, 450);
  else if (ev.captured?.length) setTimeout(playCapture, 450);
  else if (ev.to === 56) setTimeout(playHome, 450);
};

export default function LudoPracticePage() {
  const { t, tErr } = useI18n();
  const [opt, setOpt] = useState({ players: 4, pieces: 4 });
  const [g, setG] = useState(null);
  const [spins, setSpins] = useState(0);
  const [lastDie, setLastDie] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const start = useCallback(() => api.get("/ludo/practice/new", { params: opt }).then(({ data }) => { setG(data); setLastDie(null); setFailed(false); })
    .catch((e) => { setFailed(true); toast.error(tErr(errCode(e))); }), [opt, tErr]);
  useEffect(() => { start(); }, [start]);
  const send = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ludo/practice/${path}`, { state: g.state, ...body });
      if (data.event?.type === "roll") { setLastDie({ v: data.event.die, c: g.state.players[g.state.turn].color, o: data.event.outcome }); setSpins((s) => s + 2); }
      sound(data.event);
      setG(data);
    } catch (e) { toast.error(tErr(errCode(e))); } finally { setBusy(false); }
  };
  if (!g) return failed ? (
    <div className="py-24 text-center" data-testid="ludo-practice-error">
      <button type="button" onClick={start} className="h-11 rounded-full bg-amber-500 px-5 text-sm font-bold text-[#0B0D12]" data-testid="ludo-practice-retry">{t("ludo.newGame")}</button>
    </div>
  ) : null;
  const st = g.state;
  const cur = st.players[st.turn];
  const winner = st.finished && st.winners[0];
  const cname = (c) => t(`ludo.c_${c}`);
  return (
    <div className="mx-auto max-w-2xl space-y-4 rise" data-testid="ludo-practice-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-2xl font-extrabold">{t("ludo.practiceTitle")}</h1>
          <p className="text-sm text-slate-400">{t("ludo.practiceSub")}</p>
        </div>
        <SoundToggle />
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="ludo-practice-options">
        {[2, 3, 4].map((n) => <Pill key={n} on={opt.players === n} onClick={() => setOpt({ ...opt, players: n })} testId={`ludo-practice-players-${n}`}>{n} {t("ludo.players")}</Pill>)}
        <Pill on={opt.pieces === 2} onClick={() => setOpt({ ...opt, pieces: 2 })} testId="ludo-practice-pieces-2">{t("ludo.quick")} · 2 {t("ludo.pieces")}</Pill>
        <Pill on={opt.pieces === 4} onClick={() => setOpt({ ...opt, pieces: 4 })} testId="ludo-practice-pieces-4">{t("ludo.classic")} · 4 {t("ludo.pieces")}</Pill>
      </div>
      <div className="flex justify-center"><LudoTable><LudoBoard state={st} me={cur?.id} legal={g.legal} die={st.die} onPick={(i) => !busy && send("move", { piece: i })} /></LudoTable></div>
      {winner ? (
        <div className="surface flex flex-wrap items-center gap-3 p-5" data-testid="ludo-practice-end">
          <Trophy className="h-6 w-6 text-amber-400" />
          <p className="flex-1 font-display text-xl font-bold" data-testid="ludo-practice-winner">{t("ludo.practiceWin", { c: cname(winner) })}</p>
          <ShareResult game="Ludo" result={t("ludo.practiceWin", { c: cname(winner) })} path="/ludo/essai" />
          <button type="button" onClick={start} className="flex h-11 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-bold text-[#0B0D12]" data-testid="ludo-practice-again"><RotateCcw className="h-4 w-4" />{t("ludo.newGame")}</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 px-1 sm:flex-nowrap sm:gap-4" data-testid="ludo-practice-controls">
          {lastDie && <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="ludo-practice-last-roll"><Die value={lastDie.v} size={36} spins={spins} /><span>{cname(lastDie.c)}{lastDie.o === "no_move" ? ` · ${t("ludo.noMove")}` : lastDie.o === "three_sixes" ? ` · ${t("ludo.threeSixes")}` : ""}</span></div>}
          <span className="ml-auto flex items-center gap-2 text-sm font-semibold" data-testid="ludo-practice-turn"><span className="h-3 w-3 rounded-full" style={{ background: COLORS[cur.color] }} />{t("ludo.practiceTurn", { c: cname(cur.color) })}</span>
          {st.phase === "roll"
            ? <button type="button" disabled={busy} onClick={() => send("roll")} data-testid="ludo-practice-roll" className="pulse-ring h-14 rounded-full bg-amber-500 px-8 font-display text-lg font-bold text-[#0B0D12] active:scale-95 disabled:opacity-60">{t("ludo.roll")}</button>
            : <p className="text-sm text-amber-300" data-testid="ludo-practice-pick">{t("ludo.pick")}</p>}
        </div>
      )}
      <div className="flex justify-between text-sm">
        <button type="button" onClick={start} className="text-slate-400 underline" data-testid="ludo-practice-restart">{t("ludo.newGame")}</button>
        <Link to="/lobby?game=ludo" className="text-amber-300 underline" data-testid="ludo-practice-back">{t("dames.backLobby")}</Link>
      </div>
    </div>
  );
}
